import {ResourceType} from 'common/report/constants';
import {Literal} from 'common/query-builder/cohorts-query-builder/models/builder/literal';
import {
  isCohortLiteral,
  isEventLiteral,
  isPeopleLiteral,
} from 'common/query-builder/cohorts-query-builder/util/staged-cohort';
import {ALL_USERS_COHORT} from 'common/query-builder/query-block/properties-menu/constants';
import {
  CohortGroupEntry,
  CohortItem,
  FiltersOperator,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {EventFrequencyClause} from 'common/query-builder/cohorts-query-builder/models/builder/event-frequency-clause';
import {
  LogicalOperator,
  PredefinedCohort,
} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';

import {
  convertPropertyFilterToCommon,
  createCohortCommonPropertyFilter,
  getEventFiltersFromEventsFrequencyClause,
  getEventFiltersFromPeoplePropertyClause,
  getFiltersOperatorFromLiteralClauseOperators,
  getFiltersOperatorFromPropertyFilterOperator,
  getGroupingOperatorFromLogicalOperators,
} from './filters';

export const createDefaultCohortMetricEntry = (): CohortGroupEntry => ({
  type: SelectorTypeDefinitions.CohortGroup,
  event: {
    resourceType: ResourceType.Cohort,
    value: ALL_USERS_COHORT.value as string,
    label: ALL_USERS_COHORT.label,
  } as CohortItem,
  filters: [],
  filtersOperator: FiltersOperator.And,
  behavioralFiltersOperator: FiltersOperator.And,
  groupingOperator: FiltersOperator.And,
  property: null,
});

export const COHORT_METRIC_ENTRY_DEFAULT_PROPS = createDefaultCohortMetricEntry();

export function getEntryFromLiteral(
  literal: Literal,
  {
    dataGroupId,
    cohortId,
    rawCohort,
    groupingOperators = [],
    literalIndex,
  }: {
    literalIndex: number;
    dataGroupId?: string;
    cohortId?: number;
    rawCohort?: SerializedCohort;
    groupingOperators?: LogicalOperator[];
  },
): CohortGroupEntry | CohortGroupEntry[] {
  const entryDefaultProps = {
    ...createDefaultCohortMetricEntry(),
    dataGroupId: dataGroupId ?? ``,
    groupingOperator: getGroupingOperatorFromLogicalOperators(groupingOperators)[literalIndex] ?? null,
  };

  if ((literal.clauses[0] as EventFrequencyClause).behavior?.isReportBasedCohort()) {
    /** handle report based cohorts by creating a per-line cohort filter */
    return {
      ...entryDefaultProps,
      filters: [
        createCohortCommonPropertyFilter({
          cohortId,
          rawCohort,
        }),
      ],
    };
  } else if (isEventLiteral(literal)) {
    return {
      ...entryDefaultProps,
      filters: literal.clauses.map(getEventFiltersFromEventsFrequencyClause).flat(),
      filtersOperator: FiltersOperator.And,
      behavioralFiltersOperator: getFiltersOperatorFromLiteralClauseOperators(literal.clauseOperators),
    };
  } else if (isPeopleLiteral(literal)) {
    return {
      ...entryDefaultProps,
      filters: literal.clauses.map(getEventFiltersFromPeoplePropertyClause).flat(),
      filtersOperator: getFiltersOperatorFromLiteralClauseOperators(literal.clauseOperators),
    };
  } else if (isCohortLiteral(literal)) {
    // Cohort literals only have a single clause
    const [clause] = literal.clauses;
    const isAllUsers = clause.cohortId === PredefinedCohort.AllUsers;
    const clauseFilters = (clause.propertyFilters ?? []).map((filter) =>
      convertPropertyFilterToCommon(filter, clause.negated && isAllUsers),
    );
    const hasFilters = clauseFilters?.length;
    const filtersOperator = getFiltersOperatorFromPropertyFilterOperator(clause.propertyFilterOperator);

    /**
     * a cohort literal may include additional filters, these are ANDed together
     * with the cohort selector, to mimic this same behavior in the old QB
     * we create two ANDed groups which effectively merge both selectors
     */
    const cohortFilterGroup = {
      ...entryDefaultProps,
      filters: isAllUsers
        ? clauseFilters
        : [
            createCohortCommonPropertyFilter({
              cohortId: clause.cohortId,
              rawCohort,
              negated: clause.negated,
            }),
            ...clauseFilters,
          ],
      filtersOperator,
    } as CohortGroupEntry;

    /**
     * for negated cohorts with filters, the old qb behavior is merging the cohort selector
     * together with any filters, and apply a `NOT()` selector around that, we cannot achieve
     * the same thing in the new UI so we map this case to a negated top level cohort group
     */
    if (!isAllUsers && hasFilters && clause.negated && hasFilters) {
      return {
        ...cohortFilterGroup,
        filters: clauseFilters,
        event: {
          resourceType: ResourceType.Cohort,
          label: null,
          negated: true,
          value: clause.cohortId,
        },
      };
    } else if (!isAllUsers && hasFilters && filtersOperator === FiltersOperator.Or) {
      /** for cohort literals with OR filters, we create two groups joined together with AND to generate the same selector */
      return [
        {
          ...cohortFilterGroup,
          filters: [
            createCohortCommonPropertyFilter({
              cohortId: clause.cohortId,
              negated: clause.negated,
            }),
          ],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.And,
        },
        {
          ...cohortFilterGroup,
          filters: clauseFilters,
        },
      ];
    } else if (isAllUsers && hasFilters && clause.negated) {
      cohortFilterGroup.filtersOperator =
        filtersOperator === FiltersOperator.And ? FiltersOperator.Or : FiltersOperator.And;
    }

    return cohortFilterGroup;
  }

  throw new Error(`Invalid literal type`);
}
